import { DetailItem } from '@/components/DetailItem';
import { JsonViewer } from '@/components/JsonViewer';
import { UserChip } from '@/components/UserChip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime, formatRelative } from '@/util/dates';
import { type Home, type HomeLifecycle, homeLifecycle, homeMemberIds } from '@rock/types';
import type { FC } from 'react';

const LIFECYCLE_LABELS: Record<HomeLifecycle, string> = {
    'setting-up': 'Setting up',
    reviewing: 'Reviewing',
    active: 'Active',
};

export const HomeOverviewTab: FC<{ home: Home }> = ({ home }) => (
    <div className="flex flex-col gap-6">
        <Card>
            <CardHeader>
                <CardTitle>Home details</CardTitle>
                <CardDescription>Key attributes for this home.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
                <DetailItem label="Owner">
                    <UserChip userId={home.owner} size="lg" layout="stacked" showEmail link />
                </DetailItem>
                <DetailItem label="Members">
                    <div className="flex flex-wrap gap-2">
                        {homeMemberIds(home).map((userId) => (
                            <UserChip key={userId} userId={userId} size="sm" showEmail link />
                        ))}
                    </div>
                </DetailItem>
                <DetailItem label="ZIP code">{home.zip}</DetailItem>
                <DetailItem label="Status">{home.active ? 'Active' : 'Inactive'}</DetailItem>
                <DetailItem label="Lifecycle">{LIFECYCLE_LABELS[homeLifecycle(home)]}</DetailItem>
                <DetailItem label="Done adding rooms">{home.doneAddingRooms ? 'Yes' : 'No'}</DetailItem>
                <DetailItem label="Created">
                    {home.createdAt ? `${formatDateTime(home.createdAt)} (${formatRelative(home.createdAt)})` : '—'}
                </DetailItem>
                <DetailItem label="Waitlisted at">
                    {home.waitlistedAt ? formatDateTime(home.waitlistedAt) : '—'}
                </DetailItem>
                <DetailItem label="Submitted for review">
                    {home.submittedForReviewAt ? formatDateTime(home.submittedForReviewAt) : '—'}
                </DetailItem>
                <DetailItem label="Plan accepted">
                    {home.planAcceptedAt ? formatDateTime(home.planAcceptedAt) : '—'}
                </DetailItem>
                <DetailItem label="Primary location">
                    {home.geo?.city || home.geo?.state || home.geo?.country
                        ? [home.geo?.city, home.geo?.state, home.geo?.country].filter(Boolean).join(', ')
                        : '—'}
                </DetailItem>
                <DetailItem label="Last accessed">
                    {home.lastAccessedAt ? formatRelative(home.lastAccessedAt) : '—'}
                </DetailItem>
            </CardContent>
        </Card>

        <Card>
            <CardHeader>
                <CardTitle>Object</CardTitle>
            </CardHeader>
            <CardContent>
                <JsonViewer data={home} />
            </CardContent>
        </Card>
    </div>
);
